import type { Instance } from 'mobx-state-tree';
import { cast, destroy, types } from 'mobx-state-tree';

import config from '../../config';
import type { OrderingOption } from '../../types/products';
import type { VendorItem } from '../../types/shared';
import { local as localStorage } from '../../utils/storage-util';
import { withEnvironment } from '../extensions/with-environment';
import type { CustomerSnapshot } from '../models/customer';
import { CustomerModel } from '../models/customer';
import type { OrderRequestSnapshot } from '../models/order-request';
import { OrderRequestModel } from '../models/order-request';
import { OrderEventModel } from '../models/order-request/order-event';

export const OrderStore = types
  .model('OrderStore', {
    blockedOrders: types.optional(types.array(types.string), []),
    busy: types.optional(types.boolean, false),
    customer: types.maybe(CustomerModel),
    event: types.maybe(OrderEventModel),
    productId: types.maybe(types.string),
    refreshTimeout: types.maybe(types.number),
    request: types.maybe(OrderRequestModel),
    search: types.optional(
      types.model().props({
        rowsPerPage: types.optional(types.number, 6),
        term: types.optional(types.string, ''),
      }),
      {}
    ),
    setupToken: types.maybe(types.string),
    vendorId: types.maybe(types.string),
  })
  .extend(withEnvironment)
  .actions((self) => ({
    removeCard: (id: string) => {
      if (self.customer?.creditCards) {
        self.customer.creditCards = cast(
          self.customer?.creditCards.filter((card) => card.id !== id)
        );
      }
    },
    resetOrder: () => {
      self.busy = false;

      if (self.event) {
        destroy(self.event);
      }

      self.productId = undefined;

      if (self.request) {
        destroy(self.request);
      }

      self.vendorId = undefined;

      localStorage.remove(config.storageKeys.guestOrder);
    },
    setBusy: (busy = true) => {
      self.busy = busy;
    },
    setCustomer: (customer: CustomerSnapshot) => {
      self.customer = cast(customer);
    },
    setToken: (token: string) => {
      self.setupToken = token;
    },
    updated: () => {
      self.productId = undefined;
    },
  }))
  .actions((self) => ({
    getOptionsTotal: () => {
      return (
        self.request?.optionGroups
          .map((group) =>
            group.options
              .map((option) => option.price || 0)
              .reduce((a, b) => a + b, 0)
          )
          .reduce((a, b) => a + b, 0) || 0
      );
    },
    resetPickupOptions: (vendorId: string) => {
      if (self.vendorId !== vendorId) {
        console.debug('Set Pickup: ', self.vendorId, vendorId);
        self.vendorId = vendorId;

        if (self.event) {
          destroy(self.event);
        }

        if (self.request) {
          destroy(self.request);
        }
      }
    },
    setBusy: (busy = true) => {
      self.busy = busy;
    },
    startOrder: (productId: string, request: OrderRequestSnapshot) => {
      if (self.productId !== productId || !self.request) {
        console.debug('Start Order: ', self.productId, productId);
        self.productId = productId;
        self.request = cast({
          ...request,
          ...self.event,
          orderId: localStorage.get(config.storageKeys.guestOrder),
        });
      }
    },
    updateEvent: (
      vendor: VendorItem,
      entry: OperatingDayLocation,
      pickupTime: Date
    ) => {
      self.vendorId = vendor.id;
      self.event = {
        pickupTime: new Date(pickupTime),
        pickupLocationName: entry.location.name,
        pickupTimeEnd: new Date(entry.end),
        pickupTimeStart: new Date(entry.start),
        scheduleId: entry.id,
      };
    },
    updateOptions: (groupId: string, options: OrderingOption[]) => {
      if (self.request) {
        self.request = cast({
          ...self.request,
          optionGroups: self.request.optionGroups.map((group) => {
            if (group.id === groupId) {
              return {
                ...group,
                options,
              };
            }

            return group;
          }),
        });
      }
    },
    updateSpecialRequests: (requests: string) => {
      if (self.request) {
        self.request = cast({
          ...self.request,
          requests,
        });
      }
    },
  }))
  .actions((self) => ({
    block: (id: string) => {
      self.blockedOrders.push(id);
    },
    setRefreshTimeout: (timeout: number) => {
      self.refreshTimeout = timeout;
    },
    setRowsPerPage: (rows: number) => {
      self.search.rowsPerPage = rows;
    },
    setSearchTerm: (term: string) => {
      self.search.term = term;
    },
    unblock: (id: string) => {
      self.blockedOrders = cast(
        self.blockedOrders.filter((orderId) => orderId !== id)
      );
    },
  }));

type OrderStoreType = Instance<typeof OrderStore>;
export type OrderStore = OrderStoreType;
